/**
 * Income Allocations Calculator
 *
 * For LLC/partnership investors where value is tracked via tax basis:
 * Tax Basis = Contributions + Income Allocations - Distributions
 *
 * This calculator is used for entities like L&L Land North where:
 * - Contributions: Initial capital and additional capital calls
 * - Income Allocations: K-1 allocated income that increases tax basis
 * - Distributions: Cash distributed that reduces tax basis
 *
 * Also calculates Market Value from valuations (shares × price) when available.
 * Output displays Tax Basis, Market Value, and Unrealized Gain/Loss.
 */

const IncomeAllocationsCalculator = {
	methodId: 'income_allocations',
	displayName: 'Income Allocations (Tax Basis)',
	requiredDataSources: ['contributions', 'incomeAllocations', 'distributions'],

	// Graph configuration - time series line shows tax basis over time
	graphConfig: {
		recommendedType: 'time_series_line',
		timeSeriesStrategy: 'income_allocations',
		supportsTimeSeries: true,
	},

	/**
	 * Calculate tax basis from contributions, income allocations, and distributions
	 * Also calculates market value from valuations when available
	 * @param {Object} input
	 * @param {Object} input.holdings - { contributions, incomeAllocations, distributions, owned, sharePrice } by class
	 * @param {string[]} input.shareClasses - Array of share class keys
	 * @returns {Object} CalculationResult
	 */
	calculate: function (input) {
		const holdings = input.holdings || {};
		const shareClasses = input.shareClasses || Object.keys(holdings.contributions || {});

		// Ensure all required objects exist with fallback to empty objects
		const contributionsData = holdings.contributions || {};
		const incomeAllocationsData = holdings.incomeAllocations || {};
		const distributionsData = holdings.distributions || {};
		const ownedData = holdings.owned || {};
		const sharePriceData = holdings.sharePrice || {};

		let totalContributions = 0;
		let totalIncomeAllocations = 0;
		let totalDistributions = 0;
		let totalMarketValue = 0;
		const breakdown = [];

		shareClasses.forEach(function (key) {
			const contributions = contributionsData[key] || 0;
			const incomeAllocations = incomeAllocationsData[key] || 0;
			const distributions = distributionsData[key] || 0;
			const owned = ownedData[key] || 0;
			const sharePrice = sharePriceData[key] || 0;

			// Formula: Tax Basis = Contributions + Income Allocations - Distributions
			const taxBasis = contributions + incomeAllocations - distributions;

			// For LLC/partnerships, valuations represent total entity value
			// Market Value = ownership percentage × total entity value
			// owned is percentage (e.g., 9.091 = 9.091%), sharePrice is total entity value
			const marketValue = (owned / 100) * sharePrice;

			// Unrealized Gain/Loss = Market Value - Tax Basis
			const unrealizedGain = marketValue - taxBasis;

			totalContributions += contributions;
			totalIncomeAllocations += incomeAllocations;
			totalDistributions += distributions;
			totalMarketValue += marketValue;

			breakdown.push({
				classKey: key,
				className: key,
				value: taxBasis,
				taxBasis: taxBasis,
				contributions: contributions,
				incomeAllocations: incomeAllocations,
				distributions: distributions,
				owned: owned,
				sharePrice: sharePrice,
				marketValue: marketValue,
				currentValue: marketValue, // Alias for headline templates
				unrealizedGain: unrealizedGain,
			});
		});

		const totalTaxBasis = totalContributions + totalIncomeAllocations - totalDistributions;
		const totalUnrealizedGain = totalMarketValue - totalTaxBasis;

		const result = {
			totalValue: totalTaxBasis, // Primary display value (tax basis)
			totalTaxBasis: totalTaxBasis, // Explicit tax basis for templates
			totalContributions: totalContributions,
			totalIncomeAllocations: totalIncomeAllocations,
			totalDistributions: totalDistributions,
			totalMarketValue: totalMarketValue, // Market value from valuations
			totalCurrentValue: totalMarketValue, // Alias for headline templates
			totalUnrealizedGain: totalUnrealizedGain,
			breakdown: breakdown,
			fundMetrics: null, // Not applicable for this calculator type
			metadata: {
				methodUsed: this.methodId,
				calculationDate: new Date(),
			},
		};

		return result;
	},
};

export { IncomeAllocationsCalculator };
